import { defineStore } from "pinia";
import { ApiRequestError, api, postJson, setBearerToken, setCsrfToken } from "../api/client";

export type SecurityStatus = {
  status: string;
  admin_auth_enabled: boolean;
  password_auth_enabled: boolean;
  bearer_auth_enabled: boolean;
  public_bind_warning: boolean;
  locked: boolean;
  host: string;
  message: string;
};

let listenersInstalled = false;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    checked: false,
    authenticated: false,
    authRequired: false,
    authMethod: "" as "" | "session" | "bearer",
    session: null as any | null,
    security: null as SecurityStatus | null,
    error: ""
  }),
  actions: {
    async refresh() {
      try {
        const payload = await api<any>("/api/auth/session");
        this.checked = true;
        this.authenticated = Boolean(payload.authenticated);
        this.authRequired = Boolean(payload.security?.admin_auth_enabled && !payload.authenticated);
        this.authMethod = payload.authenticated ? "session" : "";
        this.session = payload.session || null;
        this.security = payload.security || null;
        setCsrfToken(payload.csrf_token || "");
        return payload;
      } catch (err) {
        this.checked = true;
        this.authenticated = false;
        this.authRequired = true;
        this.session = null;
        this.authMethod = "";
        setCsrfToken("");
        if (err instanceof ApiRequestError && err.code === "admin_locked") {
          this.security = {
            status: "locked",
            admin_auth_enabled: true,
            password_auth_enabled: false,
            bearer_auth_enabled: false,
            public_bind_warning: true,
            locked: true,
            host: "",
            message: err.message
          };
        }
        throw err;
      }
    },
    async login(password: string) {
      this.error = "";
      const payload = await postJson<any>("/api/auth/login", { password });
      this.checked = true;
      this.authenticated = true;
      this.authRequired = false;
      this.authMethod = "session";
      this.session = payload.session || null;
      this.security = payload.security || null;
      setCsrfToken(payload.csrf_token || "");
      return payload;
    },
    async loginBearer(token: string) {
      this.error = "";
      setBearerToken(token);
      try {
        const payload = await api<any>("/api/status");
        this.checked = true;
        this.authenticated = true;
        this.authRequired = false;
        this.authMethod = "bearer";
        this.session = { actor: "api-token", seconds_remaining: 0 };
        this.security = payload.security || this.security;
        setCsrfToken("");
        return payload;
      } catch (err) {
        setBearerToken("");
        this.authenticated = false;
        this.authRequired = true;
        this.authMethod = "";
        this.session = null;
        throw err;
      }
    },
    async logout() {
      if (this.authMethod !== "bearer") {
        await postJson<any>("/api/auth/logout", {});
      }
      this.authenticated = false;
      this.authRequired = Boolean(this.security?.admin_auth_enabled);
      this.authMethod = "";
      this.session = null;
      setCsrfToken("");
      setBearerToken("");
      await this.refresh().catch(() => {});
    },
    installAuthListeners() {
      if (listenersInstalled) return;
      listenersInstalled = true;
      window.addEventListener("qqpb:auth-state", ((event: CustomEvent) => {
        const state = event.detail?.state;
        const error = event.detail?.error as ApiRequestError | undefined;
        if (state === "required") {
          this.authenticated = false;
          this.authRequired = true;
          this.authMethod = "";
          this.session = null;
          setCsrfToken("");
        } else if (state === "locked") {
          this.authenticated = false;
          this.authRequired = true;
          this.authMethod = "";
          this.session = null;
          this.security = {
            status: "locked",
            admin_auth_enabled: true,
            password_auth_enabled: false,
            bearer_auth_enabled: false,
            public_bind_warning: true,
            locked: true,
            host: this.security?.host || "",
            message: error?.message || "管理后台已锁定"
          };
          setCsrfToken("");
          setBearerToken("");
        } else if (state === "csrf") {
          this.refresh().catch(() => {});
        }
      }) as EventListener);
    }
  }
});
